import { useState, useEffect, useCallback } from 'react';
import { serviceProviderService, ServiceProvider, ServiceProviderFilters, StatusUpdateResponse, UpdateServiceProviderResponse } from '../services/serviceProviderService';

export interface UseServiceProvidersReturn {
  serviceProviders: ServiceProvider[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
  stats: {
    total: number;
    enabled: number;
    disabled: number;
    blocked: number;
    monthToDate: number;
    yearToDate: number;
  } | null;
  fetchServiceProviders: (filters?: ServiceProviderFilters) => Promise<void>;
  refreshData: () => Promise<void>;
  updateFilters: (newFilters: Partial<ServiceProviderFilters>) => void;
  updateServiceProviderStatus: (mobileNumber: string, status: 'enabled' | 'disabled' | 'blocked' | 'unblocked', reason?: string) => Promise<StatusUpdateResponse>;
  updateServiceProvider: (contactNumber: string, serviceProviderType: string, categories: string[]) => Promise<UpdateServiceProviderResponse>;
  isUpdatingStatus: boolean;
  isUpdatingProvider: boolean;
}

export function useServiceProviders(initialFilters: ServiceProviderFilters = {}): UseServiceProvidersReturn {
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isUpdatingProvider, setIsUpdatingProvider] = useState(false);
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null>(null);
  const [stats, setStats] = useState<{
    total: number;
    enabled: number;
    disabled: number;
    blocked: number;
    monthToDate: number;
    yearToDate: number;
  } | null>(null);
  const [filters, setFilters] = useState<ServiceProviderFilters>({
    page: 1,
    limit: 10,
    ...initialFilters,
  });

  const fetchServiceProviders = useCallback(async (newFilters?: ServiceProviderFilters) => {
    setLoading(true);
    setError(null);

    try {
      const currentFilters = newFilters || filters;
      const response = await serviceProviderService.getServiceProviders(currentFilters);
      
      
      // Handle the new API response structure: { success: true, data: { serviceProviders: [...], pagination: {...} } }
      let dataArray: ServiceProvider[] = [];
      let paginationData = null;
      
      if (response && typeof response === 'object') {
        
        // Handle the new API response structure
        if (response.success && response.data && typeof response.data === 'object' && 'serviceProviders' in response.data) {
          const apiData = response.data as any;
          dataArray = Array.isArray(apiData.serviceProviders) ? apiData.serviceProviders : [];
          
          // Map the pagination structure from the new API response
          if (apiData.pagination) {
            paginationData = {
              page: apiData.pagination.currentPage || 1,
              limit: apiData.pagination.limit || 10,
              total: apiData.pagination.totalCount || 0,
              totalPages: apiData.pagination.totalPages || 1
            };
          }
        }
        // Handle direct array response (if API returns array directly)
        else if (Array.isArray(response)) {
          dataArray = response;
          paginationData = {
            page: 1,
            limit: response.length,
            total: response.length,
            totalPages: 1
          };
        }
        // Fallback for old response structure (if needed)
        else if (response.success !== undefined && response.data !== undefined) {
          dataArray = Array.isArray(response.data) ? response.data : [];
          paginationData = (response as any).pagination;
        }
        // If response has a different structure, try to extract data (fallback)
        else if (response.data !== undefined) {
          dataArray = Array.isArray(response.data) ? response.data : [];
          paginationData = (response as any).pagination || {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0
          };
        }
        else {
          dataArray = [];
        }
      }
      
      
      // Debug specific service provider "+1234567890" in processed data
      const specificProvider = dataArray.find(sp => sp.contactNumber === '+1234567890');
      if (specificProvider) {
      } else {
      }
      setServiceProviders(dataArray);
      if (paginationData) {
        setPagination(paginationData);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      console.error('Error fetching service providers:', err);
      // Set empty array as fallback
      setServiceProviders([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const statsData = await serviceProviderService.getServiceProviderStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching service provider stats:', err);
      // Don't set error for stats as it's not critical
    }
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([
      fetchServiceProviders(),
      fetchStats(),
    ]);
  }, [fetchServiceProviders, fetchStats]);

  const updateFilters = useCallback((newFilters: Partial<ServiceProviderFilters>) => {
    setFilters(prev => {
      const updated = { ...prev, ...newFilters };
      return updated;
    });
  }, []);

  const updateServiceProviderStatus = useCallback(async (mobileNumber: string, status: 'enabled' | 'disabled' | 'blocked' | 'unblocked', reason?: string): Promise<StatusUpdateResponse> => {
    setIsUpdatingStatus(true);
    setError(null);

    try {
      const response = await serviceProviderService.updateServiceProviderStatus(mobileNumber, status, reason);
      
      // Refresh the data to get updated status
      await refreshData();
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update service provider status';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [refreshData]);

  const updateServiceProvider = useCallback(async (contactNumber: string, serviceProviderType: string, categories: string[]): Promise<UpdateServiceProviderResponse> => {
    setIsUpdatingProvider(true);
    setError(null);

    try {
      const response = await serviceProviderService.updateServiceProvider(contactNumber, serviceProviderType, categories);
      
      // Refresh the data to get updated service provider details
      await refreshData();
      
      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update service provider details';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdatingProvider(false);
    }
  }, [refreshData]);

  // Fetch data when filters change
  useEffect(() => {
    fetchServiceProviders();
  }, [fetchServiceProviders]);

  // Fetch stats on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    serviceProviders,
    loading,
    error,
    pagination,
    stats,
    fetchServiceProviders,
    refreshData,
    updateFilters,
    updateServiceProviderStatus,
    updateServiceProvider,
    isUpdatingStatus,
    isUpdatingProvider,
  };
}
